import { CHTooltip } from '@/components/tooltip';
import { useAddToPlaylistTooltip } from '@/hooks/use-add-to-playlist-tooltip';
import { EnumButtonName } from '@calm/evexia-analytics';
import addToPlaylistMessages from '@rhg/lang/messages/add-to-playlist';
import { Box, IconButton, SxProps, Theme } from '@rhg/libs/material';
import { QueueAdd } from '@rhg/libs/material/icons';
import { KeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

const styleKeyScope = { display: 'contents' };

export interface AddToPlaylistButtonProps {
    onClick: () => void;
    sx?: SxProps<Theme>;
}

/**
 * Expanded audio player control that opens the Add to Playlists sheet. Shows a
 * one-time onboarding tooltip, marked seen on close or on tap.
 */
export function AddToPlaylistButton({ onClick, sx }: AddToPlaylistButtonProps) {
    const { formatMessage } = useIntl();
    const { isTooltipVisible, markTooltipSeen } = useAddToPlaylistTooltip();

    const button = (
        <IconButton
            size="medium"
            variant="contained"
            trackButton={EnumButtonName.AddTrackToPlaylist}
            aria-label={formatMessage(addToPlaylistMessages.buttonLabel)}
            onClick={() => {
                markTooltipSeen();
                onClick();
            }}
            sx={sx}
        >
            <QueueAdd />
        </IconButton>
    );

    // Escape on the button or in the tooltip dismisses the tooltip before it can close the player dialog.
    const handleKeyDown = (event: KeyboardEvent) => {
        if (isTooltipVisible && event.key === 'Escape') {
            event.stopPropagation();
            markTooltipSeen();
        }
    };

    // Always wrapped, so hiding the tooltip never remounts the button and focus stays on it.
    return (
        <Box component="span" sx={styleKeyScope} onKeyDown={handleKeyDown}>
            <CHTooltip
                isOpen={isTooltipVisible}
                disablePortal
                startIcon={<QueueAdd />}
                topLabel={formatMessage(addToPlaylistMessages.tooltipTitle)}
                content={formatMessage(addToPlaylistMessages.tooltipBody)}
                onClose={markTooltipSeen}
            >
                {button}
            </CHTooltip>
        </Box>
    );
}
